import { useOrderLines } from '../hooks/useOrderLines'

interface OrderTableExpandedRowProps {
  orderId: number
  colSpan: number
}

function formatKr(value: number): string {
  return value.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function OrderTableExpandedRow({ orderId, colSpan }: OrderTableExpandedRowProps) {
  const { data: lines = [], isLoading } = useOrderLines(orderId)

  if (isLoading) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-4 py-3 bg-gray-50">
          <div className="animate-pulse flex gap-4">
            <div className="h-3 w-16 bg-gray-200 rounded" />
            <div className="h-3 w-40 bg-gray-200 rounded" />
            <div className="h-3 w-12 bg-gray-200 rounded" />
          </div>
        </td>
      </tr>
    )
  }

  if (lines.length === 0) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-4 py-3 bg-gray-50 text-[12px] text-gray-400">
          Ingen ordrelinjer
        </td>
      </tr>
    )
  }

  const totalNetto = lines.reduce((sum, l) => sum + (l.amount ?? 0), 0)

  return (
    <>
      {/* Sub-header */}
      <tr className="bg-gray-50 border-b border-gray-100">
        <td colSpan={colSpan} className="px-4 py-1.5">
          <div className="flex text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            <span className="w-[80px] shrink-0">Art.nr</span>
            <span className="w-[50px] shrink-0 text-right">Antall</span>
            <span className="w-[80px] shrink-0 text-right">Enhetspris</span>
            <span className="w-[60px] shrink-0 text-right">Rabatt</span>
            <span className="w-[90px] shrink-0 text-right">Netto</span>
            <span className="w-[90px] shrink-0 text-right">Brutto</span>
            <span className="flex-1 min-w-0 pl-3">Beskrivelse</span>
          </div>
        </td>
      </tr>

      {/* Lines */}
      {lines.map((line) => (
        <tr key={line.line_id} className="bg-gray-50 border-b border-gray-100 last:border-b-0">
          <td colSpan={colSpan} className="px-4 py-1.5">
            <div className="flex items-baseline text-[12px]">
              <span className="w-[80px] shrink-0 text-gray-600 font-mono truncate">
                {line.article_no || '\u2013'}
              </span>
              <span className="w-[50px] shrink-0 text-right tabular-nums text-gray-600">
                {line.count ?? '\u2013'}
              </span>
              <span className="w-[80px] shrink-0 text-right tabular-nums text-gray-500">
                {line.price != null ? formatKr(line.price) : ''}
              </span>
              <span className="w-[60px] shrink-0 text-right tabular-nums text-gray-500">
                {line.discount != null && line.discount > 0 ? `${line.discount}%` : ''}
              </span>
              <span className="w-[90px] shrink-0 text-right tabular-nums text-gray-600">
                {line.amount != null ? formatKr(line.amount) : ''}
              </span>
              <span className="w-[90px] shrink-0 text-right tabular-nums text-gray-500">
                {line.amount != null ? formatKr(line.amount * 1.25) : ''}
              </span>
              <span className="flex-1 min-w-0 pl-3 text-gray-700 truncate">
                {line.description || '\u2013'}
                {line.info && (
                  <span className="text-gray-400 ml-1">({line.info})</span>
                )}
              </span>
            </div>
          </td>
        </tr>
      ))}

      {/* Sum row */}
      <tr className="bg-gray-100 border-b border-gray-200">
        <td colSpan={colSpan} className="px-4 py-1.5">
          <div className="flex text-[12px] font-semibold">
            <span className="w-[80px] shrink-0" />
            <span className="w-[50px] shrink-0" />
            <span className="w-[80px] shrink-0" />
            <span className="w-[60px] shrink-0 text-right text-gray-500">Sum</span>
            <span className="w-[90px] shrink-0 text-right tabular-nums text-gray-700">
              {formatKr(totalNetto)}
            </span>
            <span className="w-[90px] shrink-0 text-right tabular-nums text-gray-700">
              {formatKr(totalNetto * 1.25)}
            </span>
            <span className="flex-1 min-w-0" />
          </div>
        </td>
      </tr>
    </>
  )
}
